import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { StatCard } from "@/components/stat-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency, formatDate, getDepartmentLabel, parseLocalDate } from "@/lib/constants";
import { orpc } from "@/lib/rpc";

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery(orpc.dashboard.stats.queryOptions());
  const { data: recentApplications } = useQuery(orpc.applications.list.queryOptions());
  const { data: allJobs = [] } = useQuery(orpc.jobOpenings.list.queryOptions());
  const openJobs = allJobs.filter((job) => job.status === "open");
  const { data: pendingReviews } = useQuery(orpc.performanceCycles.list.queryOptions());
  const { data: offers } = useQuery(orpc.offers.list.queryOptions());
  const { data: pulseSurveys } = useQuery(orpc.pulseSurveys.list.queryOptions());
  const { data: employees } = useQuery(orpc.employees.list.queryOptions());

  const pendingOfferApprovals = offers?.filter((o) => o.status === "waiting_admin_approval") || [];
  const pendingJobApprovals = allJobs?.filter((j) => j.status === "pending_approval") || [];

  const urgentReviews =
    pendingReviews?.filter((r) => {
      if (r.status !== "pending" && r.status !== "in_progress") return false;
      const dueDate = parseLocalDate(r.dueDate);
      const now = new Date();
      const diffDays = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays <= 7 && diffDays >= 0;
    }) || [];

  const activeSurvey = pulseSurveys?.find((s) => s.isActive);
  const activeEmployees = employees?.filter((e) => e.status === "active") || [];
  const surveyResponses = 0;

  const recentActivity: { type: string; message: string; date: Date }[] = [];

  offers
    ?.filter((o) => o.status === "accepted")
    .slice(0, 3)
    .forEach((o) => {
      const dateValue = o.updatedAt || o.createdAt || new Date();
      recentActivity.push({
        type: "offer_accepted",
        message: `${o.application?.applicant?.fullName || "Candidato"} aceitou oferta de ${o.application?.jobOpening?.title || "posição"}`,
        date: new Date(dateValue),
      });
    });

  recentApplications?.slice(0, 3).forEach((app) => {
    const dateValue = app.createdAt || new Date();
    recentActivity.push({
      type: "application",
      message: `Nova candidatura: ${app.applicant?.fullName || "Candidato"} para ${app.jobOpening?.title || "posição"}`,
      date: new Date(dateValue),
    });
  });

  recentActivity.sort((a, b) => b.date.getTime() - a.date.getTime());
  const topActivity = recentActivity.slice(0, 5);

  const pipelineCount =
    recentApplications?.filter((a) => a.status !== "hired" && a.status !== "rejected").length || 0;

  const urgentCount =
    pendingOfferApprovals.length + pendingJobApprovals.length + urgentReviews.length;
  const importantCount = activeSurvey && activeEmployees.length - surveyResponses > 0 ? 1 : 0;
  const totalPending = urgentCount + importantCount;

  return (
    <div className="flex-1 space-y-8">
      {/* Hero — Display Section + atmospheric wash */}
      <header className="atmospheric-wash pt-2 pb-4">
        <h1 className="display-section text-foreground">Dashboard</h1>
        <p className="mt-2 text-sm text-muted-foreground">Visão geral das operações de RH</p>
      </header>

      {/* Quick metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Vagas Abertas"
          value={statsLoading ? "—" : (stats?.activeJobs ?? 0)}
          href="/jobs"
        />
        <StatCard title="Pipeline ATS" value={pipelineCount} href="/applicants" />
        <StatCard
          title="Perf. Pendente"
          value={statsLoading ? "—" : (stats?.pendingReviews ?? 0)}
          href="/performance"
        />
        <StatCard
          title="Pulse Survey"
          value={activeSurvey ? `${surveyResponses}/${activeEmployees.length}` : "N/A"}
          href="/surveys"
        />
      </div>

      {/* Pending Actions and Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Pending Actions */}
        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-baseline justify-between">
              <span>Ações Pendentes</span>
              <span className="num-mono text-xs text-muted-foreground">{totalPending}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {urgentCount > 0 && (
              <div className="space-y-2">
                <p className="caption-mono">
                  <span className="text-primary">Urgente</span>{" "}
                  <span className="num-mono text-muted-foreground">({urgentCount})</span>
                </p>
                <ul className="space-y-2">
                  {pendingOfferApprovals.map((offer) => (
                    <li key={offer.id} className="text-sm">
                      <Link
                        href="/applicants"
                        className="text-foreground hover:text-primary transition-colors"
                        data-testid={`link-pending-offer-${offer.id}`}
                      >
                        Aprovar oferta · {offer.application?.applicant?.fullName || "Candidato"}
                        {offer.application?.jobOpening?.title &&
                          ` · ${offer.application.jobOpening.title}`}
                      </Link>
                    </li>
                  ))}
                  {pendingJobApprovals.map((job) => (
                    <li key={job.id} className="text-sm">
                      <Link
                        href="/jobs"
                        className="text-foreground hover:text-primary transition-colors"
                        data-testid={`link-pending-job-${job.id}`}
                      >
                        Aprovar vaga · {job.title} · {getDepartmentLabel(job.department)}
                      </Link>
                    </li>
                  ))}
                  {urgentReviews.map((review) => (
                    <li key={review.id} className="text-sm">
                      <Link
                        href="/performance"
                        className="text-foreground hover:text-primary transition-colors"
                        data-testid={`link-urgent-review-${review.id}`}
                      >
                        Revisar performance · {review.employee?.fullName || "Colaborador"}
                        <span className="num-mono text-muted-foreground ml-1">
                          (deadline {formatDate(review.dueDate)})
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {importantCount > 0 && (
              <div className="space-y-2">
                <p className="caption-mono">
                  <span className="text-foreground">Importante</span>{" "}
                  <span className="num-mono text-muted-foreground">({importantCount})</span>
                </p>
                <ul className="space-y-2">
                  {activeSurvey && activeEmployees.length - surveyResponses > 0 && (
                    <li className="text-sm">
                      <Link
                        href="/surveys"
                        className="text-foreground hover:text-primary transition-colors"
                        data-testid="link-pulse-pending"
                      >
                        <span className="num-mono">{activeEmployees.length - surveyResponses}</span>{" "}
                        pessoas não responderam pulse survey
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {totalPending === 0 && (
              <p className="text-sm text-muted-foreground py-2">Nada pendente.</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Atividade Recente</CardTitle>
          </CardHeader>
          <CardContent>
            {topActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Nada por aqui ainda.</p>
            ) : (
              <ul className="space-y-3">
                {topActivity.map((activity, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: activity items lack stable unique id
                  <li key={index} className="border-b border-border last:border-0 pb-3 last:pb-0">
                    <p className="text-sm text-foreground">{activity.message}</p>
                    <p className="num-mono text-xs text-muted-foreground mt-1">
                      {formatDate(activity.date)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Open Positions */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="shadow-none">
          <CardHeader className="flex flex-row items-baseline justify-between pb-3">
            <CardTitle className="text-sm font-semibold">Vagas Abertas</CardTitle>
            <Link
              href="/jobs"
              className="text-xs font-semibold text-primary hover:text-[hsl(var(--comp-coral-deep))] transition-colors"
              data-testid="link-view-all-jobs"
            >
              Ver todas
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {!openJobs?.length ? (
              <p className="px-6 py-6 text-sm text-muted-foreground">Nenhuma vaga aberta.</p>
            ) : (
              <ul className="divide-y divide-border">
                {openJobs.slice(0, 4).map((job) => (
                  <li key={job.id} className="flex items-center justify-between px-6 py-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{job.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {getDepartmentLabel(job.department)}
                      </p>
                    </div>
                    <span className="num-mono text-sm font-semibold text-foreground">
                      {formatCurrency(job.salaryExpected)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
